import React, { useState } from "react";
import { toast } from "react-toastify";
import axios from "../services/httpClient";

const Feedback = () => {
  const [message, setMessage] = useState("");
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files || []);
    setImages(files);
  };

  // Convert image files to base64 data URLs
  const convertImagesToBase64 = (files) => {
    return Promise.all(
      files.map((file) => {
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!message.trim()) {
      toast.error("Please enter your feedback message.");
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert images to base64
      const base64Images =
        images.length > 0 ? await convertImagesToBase64(images) : [];

      // Prepare request payload
      const payload = {
        feedback_text: message.trim(),
        images: base64Images,
      };

      // Make API call
      const response = await axios.post("/v2/feedback", payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.status === 200 || response.status === 201) {
        toast.success("Thank you for your feedback! We'll work to improve it.");

        // Clear form
        setMessage("");
        setImages([]);

        // Reset file input value manually
        if (e.target && e.target.reset) {
          e.target.reset();
        }
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to submit feedback. Please try again.";

      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="bg-white p-4 rounded-lg w-full mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
          <h2 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
            Share Feedback & Suggestions
          </h2>
          <p className="text-gray-600 mb-6">
            We appreciate your thoughts to make Aanya better. Share any issues,
            ideas, or screenshots.
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label
                htmlFor="feedbackMessage"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Your feedback
              </label>
              <textarea
                id="feedbackMessage"
                className="w-full min-h-[140px] border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary/40"
                placeholder="Tell us what's working well and what can be improved..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                required
              />
            </div>

            <div>
              <label
                htmlFor="feedbackImages"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Attach images (optional)
              </label>
              <label
                htmlFor="feedbackImages"
                className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-primary/60 transition-colors"
              >
                <div className="flex flex-col items-center justify-center pt-5 pb-6">
                  <svg
                    className="w-8 h-8 mb-2 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <p className="mb-2 text-sm text-gray-500">
                    <span className="font-semibold text-primary">
                      Click to upload
                    </span>{" "}
                    or drag and drop
                  </p>
                  <p className="text-xs text-gray-500">
                    PNG, JPG, GIF (multiple files allowed)
                  </p>
                </div>
                <input
                  id="feedbackImages"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImagesChange}
                  className="hidden"
                />
              </label>
              {images.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-3">
                  {images.map((file, idx) => (
                    <div
                      key={idx}
                      className="relative w-20 h-20 border border-gray-200 rounded-md overflow-hidden group"
                    >
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`upload-${idx}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const newImages = images.filter((_, i) => i !== idx);
                          setImages(newImages);
                        }}
                        className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs"
                        aria-label="Remove image"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg bg-primary text-white font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Submitting..." : "Submit Feedback"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default Feedback;
